/**
 * Judges Asset Hub — public landing page at /judges
 *
 * A single URL to hand to competition judges (and to the presenter on
 * demo day). Every slide-ready MP4 and GIF lives here. Preview panels
 * autoplay inline so a judge can watch the entire journey in ~30 sec
 * without downloading anything.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Download, PlayCircle, FileVideo, FileImage, Sparkles, ArrowRight,
  ExternalLink, ShieldCheck, MessageCircle, Send, Copy, Check, FileCheck2, Building2,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const ASSETS = [
  {
    id: "altara-prologue",
    order: 0,
    title: "Prologue · why Altara exists",
    summary: "A 50-second cinematic opener in 7 beats matched to the spoken script. The TechSprint question → three alternative interventions (all place the burden on the consumer) → the burden → the different question → the conduct-problem insight → the trust layer → the upstream shift with aligned incentives for investor and institution.",
    stage: "Prologue · storyline opener",
    Icon: MessageCircle,
    duration: "~50 sec",
    mp4: "/altara-prologue.mp4",
    gif: "/altara-prologue.gif",
    live: "/wallet/demos/altara-prologue",
    embed: "/wallet/demos/altara-prologue?embed=1",
  },
  {
    id: "abc-wealth-proposal",
    order: 1,
    title: "ABC Wealth sends a proposal to Nomsa",
    summary: "A participating institution drafts a governed proposal. One tap delivers it to Nomsa's Altara wallet as a Trust Check — not an email, not an SMS.",
    stage: "Institution → Consumer",
    Icon: Send,
    duration: "~10 sec",
    mp4: "/abc-wealth-proposal.mp4",
    gif: "/abc-wealth-proposal.gif",
    live: "/wallet/demos/abc-wealth-proposal",
    embed: "/wallet/demos/abc-wealth-proposal?embed=1",
  },
  {
    id: "altara-trust-verify",
    order: 2,
    title: "Altara verifies the adviser and institution",
    summary: "Three verification checks flip green. Altara queries the FSCA Authorised Representatives register and FSP registry, then lights up the Trust icon in gold.",
    stage: "Trust stage",
    Icon: ShieldCheck,
    duration: "~9 sec",
    mp4: "/altara-trust-verify.mp4",
    gif: "/altara-trust-verify.gif",
    live: "/wallet/demos/altara-trust-verify",
    embed: "/wallet/demos/altara-trust-verify?embed=1",
  },
  {
    id: "altara-understand",
    order: 3,
    title: "Understand · contextual education tracked as signals",
    summary: "Every question Nomsa asks NAVI about the proposal is captured live on the right — hashed, timestamped, tagged. Altara then packages those signals into a personalised learning intervention pack (micro-lesson, follow-up prompt, adviser signal) and updates her learning profile in the Trust Wallet.",
    stage: "Understand stage",
    Icon: MessageCircle,
    duration: "~13 sec",
    mp4: "/altara-understand.mp4",
    gif: "/altara-understand.gif",
    live: "/wallet/demos/altara-understand",
    embed: "/wallet/demos/altara-understand?embed=1",
  },
  {
    id: "altara-check-act",
    order: 4,
    title: "Check the fit · Record the intent",
    summary: "Altara compares the monthly commitment against Nomsa's disposable income — the private context check. Then Nomsa records her intent (Ready to proceed) and a hash-chained Trust Certificate is sealed and delivered to ABC Wealth.",
    stage: "Check + Act stages",
    Icon: FileCheck2,
    duration: "~15 sec",
    mp4: "/altara-check-act.mp4",
    gif: "/altara-check-act.gif",
    live: "/wallet/demos/altara-check-act",
    embed: "/wallet/demos/altara-check-act?embed=1",
  },
  {
    id: "altara-complaint",
    order: 5,
    title: "Months later — Nomsa raises a complaint",
    summary: "Nomsa types what happened. NAVI probes to strengthen the case (amount, date, outcome sought). Altara packages the complaint into an Ombudsman-ready bundle, hash-chains it into her Trust Wallet, and delivers it to ABC Wealth.",
    stage: "Post-sale · Complaint",
    Icon: MessageCircle,
    duration: "~11 sec",
    mp4: "/altara-complaint.mp4",
    gif: "/altara-complaint.gif",
    live: "/wallet/demos/altara-complaint",
    embed: "/wallet/demos/altara-complaint?embed=1",
  },
  {
    id: "altara-trust-check-scam",
    order: 6,
    title: "Trust Check — DirectAxis loan email (non-participant)",
    summary: "Nomsa uploads a suspicious email + PDF from DirectAxis (an institution not on Altara). Trust Check analyses sender domain, offer plausibility, attachment provenance, communication channels, and greeting. Returns HIGH RISK with five named findings and a recommendation.",
    stage: "Trust & Safety · Non-participating institution",
    Icon: MessageCircle,
    duration: "~15 sec",
    mp4: "/altara-trust-check-scam.mp4",
    gif: "/altara-trust-check-scam.gif",
    live: "/wallet/demos/altara-trust-check-scam",
    embed: "/wallet/demos/altara-trust-check-scam?embed=1",
  },
  {
    id: "altara-proof",
    order: 7,
    title: "Real-world proof · not an MVP",
    summary: "8 institutional pilots · 432 investors onboarded across 6 age groups · in-branch module for elders. And a real-world case: after learning what to look for, one consumer surfaced a suspected life-insurance scheme now referred to the Financial Intelligence Centre.",
    stage: "Proof · penultimate slide",
    Icon: Building2,
    duration: "~17 sec",
    mp4: "/altara-proof.mp4",
    gif: "/altara-proof.gif",
    live: "/wallet/demos/altara-proof",
    embed: "/wallet/demos/altara-proof?embed=1",
  },
  {
    id: "altara-closing",
    order: 8,
    title: "Closing · Trust before transaction",
    summary: "One consumer. One profile. Continuously enriched. What Altara delivers to consumers, institutions and regulators — then the tagline reveal. Closes on the Altara lockup and the ethos: every decision is evidence, every consumer is a metric.",
    stage: "Closing",
    Icon: Sparkles,
    duration: "~14 sec",
    mp4: "/altara-closing.mp4",
    gif: "/altara-closing.gif",
    live: "/wallet/demos/altara-closing",
    embed: "/wallet/demos/altara-closing?embed=1",
  },
  {
    id: "nomsa-noise-orbit",
    order: 9,
    title: "Nomsa surrounded by noise (orbital)",
    summary: "Photorealistic Nomsa in the eye of the storm. Emails, X posts, Facebook ads, Instagram influencers, WhatsApp OTP-harvesters and a possibly-deepfake adviser call slowly orbit around her — each flagged with the exact Trust Check cue: suspicious link, message pattern, or manipulated media. Purely visual, no captions.",
    stage: "Aesthetic asset · pitch deck",
    Icon: MessageCircle,
    duration: "~14 sec",
    mp4: "/nomsa-noise-orbit.mp4",
    gif: "/nomsa-noise-orbit.gif",
    live: "/wallet/demos/nomsa-noise-orbit",
    embed: "/wallet/demos/nomsa-noise-orbit?embed=1",
  },
  {
    id: "nomsa-thoughts",
    order: 10,
    title: "Nomsa — what can she trust? (thought bubbles)",
    summary: "Photorealistic Nomsa surrounded by the everyday noise. Six thought bubbles pop in one-by-one around her — the exact questions she's asking herself: Is this real? Who is this adviser? Can I trust this? Is this legitimate? Is this a deepfake? What if I'm being scammed? Purely visual, no captions.",
    stage: "Aesthetic asset · the burden question",
    Icon: MessageCircle,
    duration: "~7 sec",
    mp4: "/nomsa-thoughts.mp4",
    gif: "/nomsa-thoughts.gif",
    live: "/wallet/demos/nomsa-thoughts",
    embed: "/wallet/demos/nomsa-thoughts?embed=1",
  },
  {
    id: "nomsa-actions",
    order: 11,
    title: "Altara answers the noise · three actions",
    summary: "Same chaotic scene — but Altara now calls out the three Trust Check actions ON the screens that need them: ANALYSE MESSAGES lands on the WhatsApp lure, DETECT MANIPULATED MEDIA lands on the AI-generated face, CHECK SUSPICIOUS LINKS lands on the account-suspension bit.ly phish. Each badge pops in with a gold highlight frame around its target tile. Purely visual.",
    stage: "Aesthetic asset · the Altara answer",
    Icon: ShieldCheck,
    duration: "~7 sec",
    mp4: "/nomsa-actions.mp4",
    gif: "/nomsa-actions.gif",
    live: "/wallet/demos/nomsa-actions",
    embed: "/wallet/demos/nomsa-actions?embed=1",
  },
  {
    id: "nomsa-meet",
    order: 12,
    title: "Meet Nomsa · Multiple financial relationships",
    summary: "Meet Nomsa on her still — one investor, multiple financial relationships. After 3 seconds the bold headline MULTIPLE FINANCIAL RELATIONSHIPS. lands on top of her, deliberately sized and positioned to cover her mouth so the misplaced smile does not undermine the burden line.",
    stage: "Prologue · Meet Nomsa",
    Icon: MessageCircle,
    duration: "~6 sec",
    mp4: "/nomsa-meet.mp4",
    gif: "/nomsa-meet.gif",
    live: "/wallet/demos/nomsa-meet",
    embed: "/wallet/demos/nomsa-meet?embed=1",
  },
  {
    id: "altara-journey",
    order: 13,
    title: "Nomsa's journey · full voice-over MP4 (with intro + poster)",
    summary: "Cinematic 1:22 clip opening on \"We can't control everything an investor sees. But we can change what they trust.\" — then Meet Nomsa (photo, holding her phone) — then Proposal → Verify → Understand → Check · Act. Narrated by Juniper (ElevenLabs · grounded, professional female). First frame is the opening card so PowerPoint's auto-thumbnail shows the hook line, not a blank play button. A dedicated poster JPG is also exported for manual override.",
    stage: "Master reel · one-video pitch",
    Icon: ShieldCheck,
    duration: "~1 min 22 sec",
    mp4: "/altara-journey.mp4",
    gif: "/altara-journey.mp4",
    poster: "/altara-journey-poster.jpg",
    live: "/wallet/story",
    embed: "/wallet/story",
  },
];

export default function JudgesAssetHub() {
  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(160deg, #F5EFE6 0%, #EEF2F7 55%, #E2E8F0 100%)" }}>
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 lg:py-14 space-y-8">
        <Hero />
        <FullMovieBanner />
        <div className="grid grid-cols-1 gap-6">
          {ASSETS.map(a => <AssetCard key={a.id} asset={a} />)}
        </div>
        <Roadmap />
        <Footer />
      </div>
    </div>
  );
}

function Hero() {
  return (
    <header className="text-center">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
        Altara Consumer Intelligence · TechSprint 2026
      </div>
      <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-2" style={{ color: NAVY }}>
        Nomsa&apos;s journey — for the judges
      </h1>
      <p className="text-base text-slate-600 mt-3 max-w-2xl mx-auto">
        Three cinematic beats, from proposal delivery to plain-language understanding.
        Every slide asset below is downloadable as MP4 or GIF and drops
        straight into a PowerPoint deck.
      </p>
      <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
        {ASSETS.map(a => (
          <div key={a.id} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold border"
               style={{ background: "white", borderColor: `${NAVY}22`, color: NAVY }}>
            <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] text-white"
                  style={{ background: NAVY }}>{a.order}</span>
            {a.stage}
          </div>
        ))}
      </div>
    </header>
  );
}


/* ---- Full movie banner ---- */
function FullMovieBanner() {
  return (
    <div
      data-testid="full-movie-banner"
      className="mt-8 rounded-3xl overflow-hidden border shadow-xl"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #142958 60%, #0A1F44 100%)`, borderColor: `${GOLD}66` }}
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-6 p-6 md:p-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
            The whole story · one download
          </div>
          <h2 className="font-display text-white text-2xl md:text-3xl font-semibold mt-2 leading-tight">
            All 9 slides stitched into one 3-minute movie.
          </h2>
          <p className="text-white/70 mt-2 text-[13.5px] leading-relaxed max-w-2xl">
            Prologue → Nomsa's journey → real-world proof → closing. Drop it in your PowerPoint deck as a single video,
            or share the URL for judges to preview inline.
          </p>
          <div className="mt-3 flex items-center gap-3 flex-wrap text-[11px] text-white/60">
            <span>MP4 · H.264</span><span>·</span>
            <span>1600×900 · 30 fps</span><span>·</span>
            <span>3 min 12 s</span><span>·</span>
            <span>≈ 11 MB</span>
          </div>
        </div>
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <a
            href="/altara-full-story.mp4"
            download
            data-testid="download-full-movie"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-sm"
            style={{ background: GOLD, color: NAVY }}
          >
            <Download className="w-4 h-4" /> Download the movie
          </a>
          <a
            href="/altara-full-story.mp4"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-sm border"
            style={{ borderColor: `${GOLD}88`, color: "white", background: "rgba(255,255,255,0.05)" }}
          >
            <PlayCircle className="w-4 h-4" /> Preview in browser
          </a>
        </div>
      </div>
      <div className="border-t px-6 md:px-8 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10.5px] text-white/50 uppercase tracking-[0.18em]"
           style={{ borderColor: `${GOLD}33`, background: "rgba(0,0,0,0.25)" }}>
        <span>Prologue · 0:00</span>
        <span>Proposal · 0:57</span>
        <span>Trust · 1:11</span>
        <span>Understand · 1:23</span>
        <span>Check+Act · 1:41</span>
        <span>Complaint · 1:58</span>
        <span>Trust Check · 2:11</span>
        <span>Proof · 2:30</span>
        <span>Closing · 2:52</span>
      </div>
    </div>
  );
}

function AssetCard({ asset }) {
  const [copied, setCopied] = useState(false);
  const { Icon } = asset;
  const base = window.location.origin;
  const copy = (path) => {
    navigator.clipboard?.writeText(`${base}${path}`);
    setCopied(path);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden grid grid-cols-1 lg:grid-cols-5"
         data-testid={`asset-card-${asset.id}`}>
      {/* Preview */}
      <div className="lg:col-span-3 relative bg-slate-950" style={{ minHeight: 320 }}>
        <video
          src={asset.mp4}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
          data-testid={`preview-${asset.id}`}
        />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="rounded-full px-2.5 py-1 text-[9px] tracking-[0.24em] uppercase font-semibold"
                style={{ background: NAVY, color: GOLD }}>
            {asset.order} · {asset.stage}
          </span>
        </div>
        <div className="absolute top-3 right-3 rounded-full px-2 py-1 text-[9px] tracking-widest uppercase font-semibold text-white"
             style={{ background: "rgba(0,0,0,0.5)" }}>
          {asset.duration}
        </div>
      </div>

      {/* Content */}
      <div className="lg:col-span-2 p-6 flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
               style={{ background: `${GOLD}22` }}>
            <Icon className="w-4 h-4" style={{ color: NAVY }} />
          </div>
          <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>
            Slide {asset.order}
          </div>
        </div>
        <h3 className="font-display text-lg font-semibold leading-tight" style={{ color: NAVY }}>
          {asset.title}
        </h3>
        <p className="text-[12.5px] text-slate-600 leading-relaxed mt-2">
          {asset.summary}
        </p>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <a href={asset.mp4} download
             data-testid={`dl-mp4-${asset.id}`}
             className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[11.5px] font-semibold text-white"
             style={{ background: NAVY }}>
            <FileVideo className="w-3.5 h-3.5" /> MP4
          </a>
          <a href={asset.gif} download
             data-testid={`dl-gif-${asset.id}`}
             className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[11.5px] font-semibold border"
             style={{ background: "white", borderColor: NAVY, color: NAVY }}>
            <FileImage className="w-3.5 h-3.5" /> GIF
          </a>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-2">
          <Link to={asset.live}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[11px] font-medium"
                style={{ background: `${GOLD}22`, color: NAVY }}>
            Live demo <ExternalLink className="w-3 h-3" />
          </Link>
          <Link to={asset.embed} target="_blank"
                className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[11px] font-medium"
                style={{ background: "#F1F5F9", color: NAVY }}>
            Embed view <ExternalLink className="w-3 h-3" />
          </Link>
        </div>

        <div className="mt-auto pt-3 flex items-center gap-2 text-[10px] text-slate-500">
          <button onClick={() => copy(asset.mp4)}
                  className="inline-flex items-center gap-1 hover:text-slate-700"
                  title="Copy MP4 URL">
            {copied === asset.mp4 ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
            Copy MP4 URL
          </button>
          <span>·</span>
          <button onClick={() => copy(asset.gif)}
                  className="inline-flex items-center gap-1 hover:text-slate-700"
                  title="Copy GIF URL">
            {copied === asset.gif ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
            Copy GIF URL
          </button>
        </div>
      </div>
    </div>
  );
}

function Roadmap() {
  const upcoming = [];
  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-6">
      <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>
        Coming next
      </div>
      <h3 className="font-display text-xl font-semibold mt-1" style={{ color: NAVY }}>
        Four more assets to complete Nomsa&apos;s journey
      </h3>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {upcoming.map((u, i) => (
          <div key={i}
               className="rounded-xl border border-dashed p-3 flex items-start gap-3"
               style={{ borderColor: `${NAVY}22`, background: "#FAFAF7" }}>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ background: `${NAVY}0F`, color: NAVY }}>
              <Sparkles className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: GOLD }}>
                {u.stage}
              </div>
              <div className="text-[12px] text-slate-700 leading-snug">{u.what}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="pt-4 pb-8 text-center text-[11px] text-slate-500 flex items-center justify-center gap-2">
      <div className="w-4 h-4 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
        <span className="font-display font-bold text-[9px]" style={{ color: GOLD }}>A</span>
      </div>
      altara.co · Consumer Intelligence Platform
    </footer>
  );
}
